
import React, { useState, useEffect } from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { GlassCard, Button, ProgressBar } from '../components/shared';
import { UserProfile, Language, GrowthProjection, SubjectType } from '../types';
import { generateParentReport, generateGrowthProjection } from '../services/geminiService';
import { userService } from '../services/userService';
import { Sparkles, Loader2, X, LogOut, TrendingUp, BookOpen, Brain } from 'lucide-react';
import { translations } from '../i18n';

interface ParentDashboardProps {
  user: UserProfile;
  lang: Language;
  onClose: () => void;
}

export const ParentDashboard: React.FC<ParentDashboardProps> = ({ user, lang, onClose }) => {
  const [report, setReport] = useState<{ analysis: string, suggestions: string[] } | null>(null);
  const [projection, setProjection] = useState<GrowthProjection | null>(null);
  const [activeProjSubject, setActiveProjSubject] = useState<SubjectType | 'all'>('all');
  
  const [loadingReport, setLoadingReport] = useState(false);
  const [loadingProjection, setLoadingProjection] = useState(false);
  
  const t = translations[lang];

  // Fetch projection whenever user, language, OR selected subject changes
  useEffect(() => {
    const fetchProjection = async () => {
        setLoadingProjection(true);
        // Pass the active subject to the API
        const data = await generateGrowthProjection(user, activeProjSubject, lang);
        setProjection(data);
        setLoadingProjection(false);
    };
    fetchProjection();
  }, [user, lang, activeProjSubject]);

  const handleGenerateReport = async () => {
    setLoadingReport(true);
    const data = await generateParentReport(user, lang);
    setReport(data);
    setLoadingReport(false);
  };

  const handleLogout = async () => {
    await userService.signOut();
    window.location.reload(); 
  };

  const statsData = [
    { subject: t.subjects.math, A: user.stats.math, fullMark: 100 },
    { subject: t.subjects.science, A: user.stats.science, fullMark: 100 },
    { subject: t.subjects.english, A: user.stats.english, fullMark: 100 },
    { subject: t.subjects.art, A: user.stats.art, fullMark: 100 },
    { subject: t.subjects.history, A: user.stats.history, fullMark: 100 },
    { subject: t.focus, A: Math.min(100, user.stats.focus_minutes), fullMark: 100 },
  ];

  const subjectsList: SubjectType[] = ['math', 'chinese', 'english', 'science', 'history', 'art'];

  // Check dark mode for Chart Colors
  const isDark = document.documentElement.classList.contains('dark');
  const chartTextColor = isDark ? '#94a3b8' : '#64748b';
  const chartGridColor = isDark ? '#334155' : '#cbd5e1';

  return (
    <div className="fixed inset-0 z-50 bg-slate-100/95 dark:bg-slate-950/95 backdrop-blur-sm overflow-y-auto">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
           <div className="flex items-center gap-4">
             <h2 className="text-2xl font-black text-slate-800 dark:text-white">{t.growthDashboard}</h2>
             <button 
               onClick={handleLogout}
               className="flex items-center gap-2 px-3 py-1 bg-red-50 dark:bg-red-500/10 text-red-500 dark:text-red-400 rounded-lg text-xs font-bold hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors"
             >
               <LogOut size={14}/> {t.logout}
             </button>
           </div>
           <button onClick={onClose} className="p-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-white rounded-full shadow-sm hover:bg-slate-200 dark:hover:bg-slate-700"><X size={24}/></button>
        </div>

        {/* Top Area: Radar & Projection */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
           {/* Left: Radar */}
           <GlassCard className="lg:col-span-1 flex flex-col items-center justify-between" variant="light">
             <h3 className="font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest text-xs mb-4 self-start">{t.cognitiveRadar}</h3>
             <div className="h-[300px] w-full">
               <ResponsiveContainer width="100%" height="100%">
                 <RadarChart cx="50%" cy="50%" outerRadius="80%" data={statsData}>
                   <PolarGrid stroke={chartGridColor} />
                   <PolarAngleAxis dataKey="subject" tick={{ fill: chartTextColor, fontSize: 12, fontWeight: 'bold' }} />
                   <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false}/>
                   <Radar name={user.name} dataKey="A" stroke="#6366f1" strokeWidth={3} fill="#6366f1" fillOpacity={0.4} />
                 </RadarChart>
               </ResponsiveContainer>
             </div>
             <div className="grid grid-cols-3 gap-4 w-full mt-4 border-t border-slate-100 dark:border-white/5 pt-4">
               <div className="text-center">
                 <div className="text-xl font-black text-slate-800 dark:text-white">{user.stats.focus_minutes}</div>
                 <div className="text-[10px] text-slate-400 font-bold uppercase">{t.focusTime}</div>
               </div>
               <div className="text-center">
                 <div className="text-xl font-black text-slate-800 dark:text-white">{user.stats.tasks_completed}</div>
                 <div className="text-[10px] text-slate-400 font-bold uppercase">{t.tasksDone}</div>
               </div>
               <div className="text-center">
                 <div className="text-xl font-black text-slate-800 dark:text-white">{user.skills.length}</div>
                 <div className="text-[10px] text-slate-400 font-bold uppercase">{t.skillsUnlocked}</div>
               </div>
             </div>
           </GlassCard>

           {/* Right: Growth Projection & Insight */}
           <div className="lg:col-span-2 space-y-6">
              {/* Projection Card */}
              <GlassCard className="relative overflow-hidden min-h-[300px]" variant="light">
                <div className="flex items-center gap-2 mb-6 border-b border-slate-100 dark:border-white/5 pb-4">
                  <TrendingUp className="text-indigo-500" size={20}/>
                  <h3 className="font-bold text-slate-800 dark:text-white text-lg">{t.growthProjection}</h3>
                </div>
                
                {/* Tabs for switching subject */}
                <div className="flex gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide">
                  <button 
                    onClick={() => setActiveProjSubject('all')}
                    className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${activeProjSubject === 'all' ? 'bg-slate-800 dark:bg-white text-white dark:text-slate-900' : 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                  >
                    {t.allSubjects}
                  </button>
                  {subjectsList.map(s => (
                    <button 
                      key={s} 
                      onClick={() => setActiveProjSubject(s)}
                      className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all ${activeProjSubject === s ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'}`}
                    >
                      {t.subjects[s]}
                    </button>
                  ))}
                </div>

                {/* Timeline Visualization */}
                {loadingProjection ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mb-4"/>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t.analyzing}</p>
                    </div>
                ) : (
                    <div className="animate-in fade-in duration-500">
                      {/* Timeline Graphic */}
                      <div className="relative px-4 pb-8 mb-4">
                         {/* Line */}
                         <div className="absolute top-3 left-8 right-8 h-1 bg-slate-100 dark:bg-slate-700 rounded-full z-0"></div>
                         
                         <div className="grid grid-cols-4 gap-2 relative z-10">
                            {/* Current */}
                            <div className="flex flex-col items-center text-center">
                               <div className="w-7 h-7 bg-slate-300 dark:bg-slate-600 rounded-full border-4 border-white dark:border-slate-800 shadow-sm mb-3"></div>
                               <div className="text-xs text-slate-400 font-bold mb-1">{t.current}</div>
                               <div className="text-sm font-bold text-slate-800 dark:text-white">{projection?.timeline?.current}</div>
                            </div>
                            
                            {/* 3 Months */}
                            <div className="flex flex-col items-center text-center">
                               <div className="w-7 h-7 bg-indigo-200 dark:bg-indigo-900 rounded-full border-4 border-white dark:border-slate-800 shadow-sm mb-3"></div>
                               <div className="text-xs text-slate-400 font-bold mb-1">{t.threeMonths}</div>
                               <div className="text-sm font-bold text-indigo-700 dark:text-indigo-300">{projection?.timeline?.threeMonth}</div>
                            </div>

                            {/* 1 Year */}
                            <div className="flex flex-col items-center text-center">
                               <div className="w-7 h-7 bg-indigo-400 dark:bg-indigo-600 rounded-full border-4 border-white dark:border-slate-800 shadow-sm mb-3"></div>
                               <div className="text-xs text-slate-400 font-bold mb-1">{t.oneYear}</div>
                               <div className="text-sm font-bold text-indigo-700 dark:text-indigo-300">{projection?.timeline?.oneYear}</div>
                            </div>

                            {/* 3 Years */}
                            <div className="flex flex-col items-center text-center">
                               <div className="w-7 h-7 bg-rose-400 dark:bg-rose-600 rounded-full border-4 border-white dark:border-slate-800 shadow-sm mb-3"></div>
                               <div className="text-xs text-slate-400 font-bold mb-1">{t.threeYears}</div>
                               <div className="text-sm font-bold text-rose-600 dark:text-rose-400">{projection?.timeline?.threeYear}</div>
                            </div>
                         </div>
                      </div>

                      {/* Cognitive Stage Card */}
                      {projection?.cognitive_stage && (
                        <div className="bg-indigo-50/50 dark:bg-indigo-900/20 rounded-2xl p-5 border border-indigo-100 dark:border-indigo-500/20 relative overflow-hidden">
                           <div className="absolute -right-4 -top-4 text-indigo-100 dark:text-indigo-900 opacity-50">
                             <Brain size={120} />
                           </div>
                           <div className="relative z-10 flex gap-4">
                             <div className="mt-1 text-indigo-600 dark:text-indigo-400">
                               <Brain size={24} />
                             </div>
                             <div>
                               <h4 className="font-bold text-indigo-900 dark:text-indigo-100 text-base mb-1">
                                 {t.cognitiveStage}: {projection.cognitive_stage.name}
                               </h4>
                               <p className="text-xs font-bold text-indigo-600 dark:text-indigo-300 mb-3 uppercase tracking-wider">
                                 {t.keyFocus}: {projection.cognitive_stage.focus}
                               </p>
                               <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed italic">
                                 "{projection.cognitive_stage.desc}"
                               </p>
                             </div>
                           </div>
                        </div>
                      )}

                      <div className="mt-4 text-center">
                        <span className="text-[10px] text-slate-300 dark:text-slate-600 font-bold tracking-[0.2em] uppercase">{t.projectionDisclaimer}</span>
                      </div>
                    </div>
                )}
              </GlassCard>

              {/* AI Insight (Original) */}
              <GlassCard className="bg-gradient-to-br from-indigo-50 to-white dark:from-indigo-950 dark:to-slate-900 border-indigo-100 dark:border-white/10" variant="light">
                 <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-indigo-900 dark:text-indigo-100 flex items-center gap-2 text-sm"><Sparkles size={16}/> {t.aiInsight}</h3>
                    {!report && <Button variant="secondary" onClick={handleGenerateReport} className="py-1 px-3 text-xs h-auto" disabled={loadingReport}>{loadingReport ? t.analyzing : t.genReport}</Button>}
                 </div>
                 {report && (
                   <div className="text-sm text-slate-700 dark:text-slate-200 bg-white/50 dark:bg-black/20 p-4 rounded-xl border border-indigo-50 dark:border-white/5 mt-2 animate-in fade-in">
                      <p className="font-medium mb-2">{report.analysis}</p>
                      <ul className="list-disc pl-4 space-y-1 text-slate-500 dark:text-slate-400 text-xs">
                        {report.suggestions.map((s,i) => <li key={i}>{s}</li>)}
                      </ul>
                   </div>
                 )}
              </GlassCard>
           </div>
        </div>

        {/* Bottom Area: Subject Details */}
        <GlassCard variant="light">
           <div className="flex items-center gap-2 mb-6">
              <BookOpen className="text-slate-500 dark:text-slate-400" size={20}/>
              <h3 className="font-bold text-slate-800 dark:text-white text-lg">{t.subjectDetails}</h3>
           </div>
           <div className="space-y-4">
              {subjectsList.map((subject) => {
                const score = user.stats[subject] || 0;
                const max = 100; // Mock max per level
                const colors: Record<string, string> = {
                  math: 'bg-blue-500', chinese: 'bg-red-500', english: 'bg-violet-500',
                  science: 'bg-emerald-500', history: 'bg-amber-500', art: 'bg-pink-500'
                };
                const icons: Record<string, string> = {
                  math: '🔢', chinese: '📜', english: '🔤', science: '🔬', history: '🏛️', art: '🎨'
                };

                return (
                  <div key={subject} className="flex items-center gap-4 p-2 hover:bg-slate-50 dark:hover:bg-white/5 rounded-xl transition-colors">
                     <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 shadow-sm`}>
                       {icons[subject]}
                     </div>
                     <div className="w-24 font-bold text-slate-700 dark:text-slate-200 text-sm">{t.subjects[subject]}</div>
                     <div className="flex-1">
                        <ProgressBar current={score} max={max} color={colors[subject]} className="h-3"/>
                     </div>
                     <div className="w-12 text-right font-black text-slate-400 dark:text-slate-500">{score}</div>
                  </div>
                );
              })}
           </div>
        </GlassCard>
      </div>
    </div>
  );
};
